import type { IAirtablePromptBase } from "~/js/network/airtable-prompt-base";
import EventAnalytics from "~/js/utilities/EventAnalytics";
import { $div } from "~/js/utilities/TurboDom";
import { getUserStore } from "~/solidJs/entities/HeaderMain/models";
import { ChatGptApi } from "~/solidJs/shared/api/ChatGptApi";
import {
	debugNonLatinCharacters,
	getIsSaveFunctionalityAvailable,
	getPrivateMessageFromError,
	hasNonLatinCharacters,
	isLatinText,
	pickRandomFromArray,
	recalculateTextAreaHeight,
	shouldShowPremiumLock
} from "~/solidJs/shared/helpers";
import { openPricingPage } from "~/solidJs/shared/models/pageParams/pricingPageParams";
import { OverlayTooltipMethods } from "~/solidJs/widgets/OverlayTooltip";
import {
	showToastMessage,
	ToastMessageTypes
} from "~/solidJs/widgets/WorkspaceToast/WorkspaceToast";
import type { Socket } from "./node-socket-connection";

type IGenerationsType = "promptExtend" | "randomizePrompt";

const showRandomPromptToastMessage = () => {
	showToastMessage({ title: "Random prompt added", timeout: 3000 });
};
const showImprovePromptToastMessage = () => {
	showToastMessage({ title: "Prompt improved successfully", timeout: 3000 });
};
const showAlreadyOptimizedError = () => {
	showToastMessage({
		title: "Text is already improved",
		type: ToastMessageTypes.Warning,
		timeout: 3000
	});
};

export class PromptHelper {
	Dom: {
		[key: string]: HTMLElement;
	};
	globalId: string;
	prompt: string;
	generationType: IGenerationsType = "randomizePrompt";
	lastOperation: IGenerationsType | "undo" = "randomizePrompt";
	inputToAppend: HTMLInputElement;
	tempPrompt: string;
	isGeneration: boolean;
	initPlaceholder: string;
	getPromptHelperSource: () => Promise<IAirtablePromptBase>;
	isUserPromptSended = false;
	isInitPrompt = false;
	externalShowError?: (msg: string) => void;
	externalHideError?: () => void;
	isTranslating: boolean = false;
	translateAbortController: AbortController | null = null;
	constructor(
		inputToAppend: HTMLInputElement,
		nodeGlobalId: string,
		getPromptHelperSource: () => Promise<IAirtablePromptBase>,
		public socket: Socket,
		existingUiStructure?: any
	) {
		this.Dom = {};
		this.getPromptHelperSource = getPromptHelperSource;

		this.inputToAppend = inputToAppend;
		this.initPlaceholder = inputToAppend.placeholder;
		this.isGeneration = false;
		this.tempPrompt = inputToAppend.value;
		this.prompt = inputToAppend.value;
		this.initDom(inputToAppend, existingUiStructure);
		this.initEventListners();
		this.initToolTips();
		this.globalId = nodeGlobalId;
		this.toggleGenerationMode(false);
		setTimeout(() => {
			this.initPrompt();
		}, 0);
	}
	setInputValue(value: string) {
		this.inputToAppend.value = value;
		this.socket.fireChange();
	}
	async initPrompt() {
		if (this.inputToAppend.value) return;
		const randomPrompt = await this.randomizePrompt(false);
		if (this.inputToAppend.value) return;
		this.isInitPrompt = true;
		this.inputToAppend.placeholder = randomPrompt;
		this.prompt = randomPrompt;
	}
	async startGeneratingPrompt(type: IGenerationsType) {
		this.toggleGenerationMode(true);

		await this.generatePrompt(type);
	}
	initDom(inputToAppend: HTMLInputElement, existingUiStructure?: any) {
		if (existingUiStructure) {
			// Use existing UI structure from data-entry
			this.Dom.mainContainer = existingUiStructure.mainContainer;
			this.Dom.inputTitle = existingUiStructure.inputTitle;
			this.Dom.domElement = existingUiStructure.domElement;
			this.Dom.iconContainer = existingUiStructure.iconContainer;
			this.Dom.errorContainer = existingUiStructure.errorContainer;

			// Store references to external error handling methods
			this.externalShowError = existingUiStructure.showError;
			this.externalHideError = existingUiStructure.hideError;
		} else {
			// Create new UI structure (backward compatibility)
			this.Dom.mainContainer = $div("mainContainer");
			this.Dom.inputTitle = $div("inputTitle", this.Dom.mainContainer);
			this.Dom.inputTitle.textContent = this.socket.definition.getName();
			this.Dom.domElement = $div("generatePromptContainer", this.Dom.mainContainer);
			this.Dom.iconContainer = $div("iconContainer", this.Dom.domElement);
			this.Dom.errorContainer = $div("errorContainer", this.Dom.domElement);
			this.Dom.domElement.append(inputToAppend);
		}

		// Create buttons container and buttons (always needed)
		this.Dom.buttonsGenerationsContainer = $div(
			"buttonsGenerationsContainer",
			this.Dom.domElement
		);

		// Create containers for button groups
		this.Dom.buttonsPrimaryContainer = $div("buttons-primary-container");
		this.Dom.buttonsSecondaryContainer = $div("buttons-secondary-container");

		// Create and configure primary buttons
		this.Dom.promptExtend = this.getBtn("promptExtend");
		if (shouldShowPremiumLock(getUserStore())) {
			this.Dom.promptExtend.classList.add("premium");
		}
		this.Dom.randomizePrompt = this.getBtn("randomizePrompt");
		this.Dom.promptExtend.textContent = "Improve";
		this.Dom.randomizePrompt.textContent = "Random";

		// Create and configure secondary button (undo)
		this.Dom.undoBtn = this.getBtn("undo");
		this.Dom.undoBtn.textContent = "Undo";
		this.initUndoBtn(this.Dom.undoBtn);

		// Create translate button - add to primary buttons container (next to Random and Improve)
		const translateBtn = document.createElement("button");
		translateBtn.className = "translate-btn";
		translateBtn.textContent = "Translate";
		translateBtn.style.display = "none";
		translateBtn.type = "button";
		this.Dom.translateBtn = translateBtn;

		// Add buttons to their respective containers
		this.Dom.buttonsPrimaryContainer.append(this.Dom.randomizePrompt);
		this.Dom.buttonsPrimaryContainer.append(this.Dom.promptExtend);
		this.Dom.buttonsPrimaryContainer.append(this.Dom.translateBtn);
		this.Dom.buttonsSecondaryContainer.append(this.Dom.undoBtn);

		// Add containers to the main buttons container
		this.Dom.buttonsGenerationsContainer.append(this.Dom.buttonsPrimaryContainer);
		this.Dom.buttonsGenerationsContainer.append(this.Dom.buttonsSecondaryContainer);

		// Initialize error state
		this.hideError();

		// Check initial value for non-Latin characters
		setTimeout(() => {
			this.checkAndShowTranslateButton();
		}, 0);

		return this.Dom.mainContainer;
	}

	showError(msg: string) {
		if (this.externalShowError) {
			this.externalShowError(msg);
		} else {
			this.Dom.domElement.classList.add("error");
			this.Dom.errorContainer.textContent = msg;
			this.Dom.errorContainer.classList.remove("hidden");
		}
	}

	hideError() {
		if (this.externalHideError) {
			this.externalHideError();
		} else {
			this.Dom.domElement.classList.remove("error");
			this.Dom.errorContainer.classList.add("hidden");
		}
	}

	initUndoBtn(btn: HTMLElement) {
		this.hideUndoBtn(true);
		btn.addEventListener("click", () => {
			this.hideUndoBtn(true);
			this.lastOperation = "undo";
			this.setInputValue(this.tempPrompt);
			recalculateTextAreaHeight(this.inputToAppend);

			// Check if restored text contains non-Latin characters
			this.checkAndShowTranslateButton();
		});
	}
	hideUndoBtn(isHidden: boolean) {
		this.Dom.undoBtn.classList.toggle("hidden", isHidden);
	}

	initToolTips() {
		const cleanupExtend = OverlayTooltipMethods.attachOverlayTooltip(
			this.Dom.promptExtend,
			"Improve my prompt"
		);
		const cleanupRandomize = OverlayTooltipMethods.attachOverlayTooltip(
			this.Dom.randomizePrompt,
			"New prompt idea"
		);
		const cleanupTranslate = OverlayTooltipMethods.attachOverlayTooltip(
			this.Dom.translateBtn,
			"This node works with English only.\nClick to translate your text"
		);

		// Cleanup tooltips when node is removed
		this.socket.node.eventor.addListener("remove", () => {
			cleanupExtend();
			cleanupRandomize();
			cleanupTranslate();
		});
	}

	replaceValueWithPrompt() {
		if (!this.isInitPrompt) return;

		this.setInputValue(this.prompt);
		recalculateTextAreaHeight(this.inputToAppend);
		this.toggleGenerationMode(false);
	}

	clear() {
		this.toggleGenerationMode(false);
	}

	initEventListners() {
		this.Dom.promptExtend.addEventListener("click", () => {
			const canUse = getIsSaveFunctionalityAvailable(getUserStore());
			if (!canUse) {
				EventAnalytics.disabledPromptHelperExtend();
				openPricingPage({ source: "promptHelperExtend" });
				return;
			}
			this.startGeneratingPrompt("promptExtend");
		});
		this.Dom.randomizePrompt.addEventListener("click", () => {
			this.startGeneratingPrompt("randomizePrompt");
		});

		this.inputToAppend.addEventListener("input", () => {
			this.isInitPrompt = false;
			if (!this.isUserPromptSended) {
				this.isUserPromptSended = true;
				EventAnalytics.promptHelper("userPrompt");
			}
			this.lastOperation = "randomizePrompt";
			this.hideError();
			this.hideUndoBtn(true);
			this.toggleGenerationMode(false);
			this.inputToAppend.placeholder = this.initPlaceholder;

			// Check if text contains non-Latin characters and show translate button
			this.checkAndShowTranslateButton();
		});

		// Also check on paste event
		this.inputToAppend.addEventListener("paste", () => {
			setTimeout(() => {
				this.checkAndShowTranslateButton();
			}, 0);
		});

		// Translate button click handler
		this.Dom.translateBtn.addEventListener("click", async (e) => {
			e.preventDefault();
			e.stopPropagation();
			await this.translateText();
		});
	}

	async generatePrompt(generationType: IGenerationsType) {
		this.inputToAppend.placeholder = this.initPlaceholder;
		this.isInitPrompt = false;
		EventAnalytics.promptHelper(generationType);
		this.hideError();
		const prompt = await this[generationType]?.();
		if (!prompt) return;
		if (!this.isGeneration) return;
		this.lastOperation = generationType;
		this.tempPrompt = this.inputToAppend.value;
		this.setInputValue(prompt);
		recalculateTextAreaHeight(this.inputToAppend);
		this.prompt = prompt || "";
		this.hideUndoBtn(false);

		// Check if generated prompt contains non-Latin characters
		this.checkAndShowTranslateButton();
	}

	async promptExtend() {
		if (!this.inputToAppend.value) {
			this.showError("Enter text to improve prompt");
			return;
		}
		if (this.lastOperation === "promptExtend") {
			showAlreadyOptimizedError();
			return;
		}
		this.togglePending(true);

		try {
			const { message } = await ChatGptApi.getChatGpt4oPromptWithoutTask(
				this.inputToAppend.value
			);
			this.togglePending(false);
			showImprovePromptToastMessage();
			return message;
		} catch (e) {
			EventAnalytics.sendErrorInfo("prompt-helper-gpt-error", {
				message: getPrivateMessageFromError(e)
			});
			this.togglePending(false);
			return;
		}
	}

	async randomizePrompt(showMessage = true) {
		const prompts = await this.getPromptHelperSource();
		let prompt: string;
		if (this.globalId in prompts) {
			prompt = pickRandomFromArray(prompts[this.globalId]).prompt;
		} else {
			prompt = pickRandomFromArray(prompts.basePrompts).prompt;
		}
		if (showMessage) {
			showRandomPromptToastMessage();
		}
		return prompt;
	}

	getBtn(name: string) {
		const btn = document.createElement("button");
		btn.classList.add(name);
		return btn;
	}

	togglePending(isPending: boolean) {
		this.Dom.mainContainer.classList.toggle("promptGenerating", isPending);
	}

	toggleGenerationMode(isGeneration: boolean) {
		if (isGeneration) {
			this.isUserPromptSended = false;
		}
		this.isGeneration = isGeneration;
	}

	domElementOfButtonsGenerations() {
		return this.Dom.buttonsGenerationsContainer;
	}

	domElement() {
		return this.Dom.mainContainer;
	}

	/**
	 * Check if text contains non-Latin characters and show/hide translate button
	 * Only shows translate button if node requires English-only input (based on Airtable field)
	 */
	checkAndShowTranslateButton() {
		// Only show translate button for nodes that require English-only input
		if (!this.socket.node.definition.isEnglishOnly) {
			this.hideTranslateButton();
			return;
		}

		const text = this.inputToAppend.value.trim();

		if (!text) {
			this.hideTranslateButton();
			this.hideError();
			return;
		}

		// Debug logging
		const isLatin = isLatinText(text);
		const hasNonLatin = hasNonLatinCharacters(text);
		const nonLatinChars = debugNonLatinCharacters(text);

		console.log("[PromptHelper] Checking text for non-Latin characters:", {
			textLength: text.length,
			isLatinText: isLatin,
			hasNonLatinCharacters: hasNonLatin,
			nonLatinCharsFound: nonLatinChars.length,
			nonLatinChars: nonLatinChars.length > 0 ? nonLatinChars : undefined,
			textPreview: text.substring(0, 100) + (text.length > 100 ? "..." : "")
		});

		if (nonLatinChars.length > 0) {
			console.log(
				"[PromptHelper] Non-Latin characters details:",
				nonLatinChars.map((char) => ({
					char: char.char,
					position: char.position,
					charCode: char.charCode,
					charCodeHex: `0x${char.charCodeHex}`,
					context: char.context
				}))
			);
		}

		if (hasNonLatinCharacters(text)) {
			this.showTranslateButton();
			this.showError("This node supports English-language prompts only.");
		} else {
			this.hideTranslateButton();
			this.hideError();
		}
	}

	/**
	 * Show translate button
	 */
	showTranslateButton() {
		if (this.Dom.translateBtn) {
			this.Dom.translateBtn.style.display = "block";
		}
	}

	/**
	 * Hide translate button
	 */
	hideTranslateButton() {
		if (this.Dom.translateBtn) {
			this.Dom.translateBtn.style.display = "none";
		}
	}

	/**
	 * Translate text to English
	 */
	async translateText() {
		if (this.isTranslating) {
			return;
		}

		const textToTranslate = this.inputToAppend.value.trim();
		if (!textToTranslate) {
			return;
		}

		try {
			this.isTranslating = true;
			this.translateAbortController = new AbortController();

			// Update button to show loading state
			const translateBtn = this.Dom.translateBtn as HTMLButtonElement;
			translateBtn.disabled = true;
			translateBtn.textContent = "Translating...";

			// Call translation API
			const translatedText = await ChatGptApi.translateToEnglish(
				textToTranslate,
				this.translateAbortController.signal
			);

			// Update input value
			this.setInputValue(translatedText);
			recalculateTextAreaHeight(this.inputToAppend);

			// Show success message
			showToastMessage({
				title: "Text translated successfully",
				timeout: 3000
			});

			// Hide translate button and error
			this.hideTranslateButton();
			this.hideError();
		} catch (error) {
			console.error("Translation error:", error);

			// Show error message
			showToastMessage({
				title: "Failed to translate text",
				type: ToastMessageTypes.Error,
				timeout: 3000
			});

			this.showError("Translation failed. Please try again.");
		} finally {
			this.isTranslating = false;
			this.translateAbortController = null;

			// Restore button state
			const translateBtn = this.Dom.translateBtn as HTMLButtonElement;
			translateBtn.disabled = false;
			translateBtn.textContent = "Translate";
		}
	}
}
